import { cn } from '../lib/cn';
import { cloneElement, isValidElement, Children, forwardRef } from 'react';

const variants = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  ghost: 'hover:bg-accent hover:text-accent-foreground',
  link: 'text-primary underline-offset-4 hover:underline',
} as const;

const sizes = {
  default: 'h-10 px-4 py-2',
  sm: 'h-9 rounded-md px-3',
  lg: 'h-11 rounded-md px-8',
  icon: 'h-10 w-10',
} as const;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  asChild?: boolean;
}

// forwardRef — 드롭다운 트리거처럼 위치 측정·포커스 복귀가 필요한 곳에서 참조가 필요하다.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'default', size = 'default', asChild = false, ...props },
  ref
) {
  const classes = cn(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    variants[variant],
    sizes[size],
    className
  );

  // NOTE: asChild 경로는 className 만 자식에게 넘긴다.
  // ref 는 물론 onClick/disabled/type/aria-* 등 나머지 props 도 전달되지 않는다.
  // (아래 cloneElement 의 config 가 `{ className }` 뿐이다)
  // asChild 를 쓸 때는 필요한 props 를 자식 요소에 직접 달 것.
  if (asChild) {
    const child = Children.only(props.children);
    if (isValidElement(child)) {
      return cloneElement(child as React.ReactElement<{ className?: string }>, {
        className: cn(classes, (child.props as { className?: string }).className),
      });
    }
  }

  return <button ref={ref} className={classes} {...props} />;
});
